import { create } from 'zustand';

// Within-session restoration intent for the /bible index (Navigation overhaul
// chunk C, README §6.1). NOT IDB: reload → fresh clean index is acceptable. The
// snapshot is SINGLE-USE: written only on an outbound-from-index navigation
// (§6.2) and CLEARED on the next /bible mount after it restores (consume), with
// a belt-and-braces clear on the PrayPage (/) mount so a route through Home can
// never leak a stale snapshot into a fresh Bible visit (Blocker 4). Mirrors the
// bsb-progress-store pattern: a tiny dedicated slice kept separate from the main
// app store so route-restoration concerns do not entangle with prayer/settings.
export type BibleIndexIntent = 'browse' | 'search' | 'neutral';

export interface BibleIndexSnapshot {
  expandedSlugs: string[]; // FULL expanded-book set at exit (LOW-1)
  activeBookSlug: string | null; // the book whose chapter was opened (browse anchor, B5)
  query: string; // active query at exit ('' for browse/neutral)
  intent: BibleIndexIntent;
}

interface BibleIndexViewState {
  snapshot: BibleIndexSnapshot | null;
  setSnapshot: (s: BibleIndexSnapshot) => void;
  clearSnapshot: () => void;
}

export const useBibleIndexViewStore = create<BibleIndexViewState>((set) => ({
  snapshot: null,
  setSnapshot: (snapshot) => set({ snapshot }),
  clearSnapshot: () => set({ snapshot: null }),
}));

// Non-reactive snapshot accessors (mirrors settings-store's getX() helpers).
// BibleBookIndex reads the snapshot ONCE at mount via a pure read (no clear),
// then consumes it in a mount effect; outbound handlers write; PrayPage clears.
export const readBibleIndexSnapshot = (): BibleIndexSnapshot | null =>
  useBibleIndexViewStore.getState().snapshot;
export const writeBibleIndexSnapshot = (s: BibleIndexSnapshot): void =>
  useBibleIndexViewStore.getState().setSnapshot(s);
export const clearBibleIndexSnapshot = (): void =>
  useBibleIndexViewStore.getState().clearSnapshot();
